import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AdminInbox from "../components/AdminInbox";
import { MemberDrawer, MembersTab, PurchasesTab } from "../components/AdminViews";
import { API } from "../config";
import { Users, ArrowLeft, Sparkles, Loader2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";

const token = () => localStorage.getItem("session_token");
const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token()}` });
const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination); o.frequency.value = 880;
    g.gain.setValueAtTime(0.2, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    o.start(); o.stop(ctx.currentTime + 0.4);
  } catch { /* audio indisponible */ }
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const [denied, setDenied] = useState(false);
  const [count, setCount] = useState(900);
  const [genMode, setGenMode] = useState("total");
  const [tab, setTab] = useState("conversations");
  const [memberFilter, setMemberFilter] = useState("all");
  const [inboxFilter, setInboxFilter] = useState("all");
  const [openMember, setOpenMember] = useState(null);
  const goMembers = (f) => { setMemberFilter(f); setTab("members"); };
  const goInbox = (f) => { setInboxFilter(f); setTab("conversations"); };
  const [job, setJob] = useState(null);
  const [aiCount, setAiCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [showGallery, setShowGallery] = useState(false);
  const prevUnread = useRef(null);
  const [engagement, setEngagement] = useState(null);

  const loadCount = async () => {
    const r = await fetch(`${API}/admin/ai-profiles-count`, { headers: authHeaders() });
    if (r.ok) setAiCount((await r.json()).ai_profiles);
  };
  const loadStats = async () => {
    const r = await fetch(`${API}/admin/stats`, { headers: authHeaders() });
    if (r.status === 403) { setDenied(true); return; }
    if (!r.ok) return;
    const d = await r.json();
    setStats(d);
    if (prevUnread.current !== null && d.admin_unread > prevUnread.current) {
      toast.success(`${d.admin_unread - prevUnread.current} nouveau(x) message(s) reçu(s)`);
      beep();
    }
    prevUnread.current = d.admin_unread;
    if (d.generation?.status === "running") setJob(d.generation);
    document.title = d.admin_unread > 0 ? `(${d.admin_unread}) Admin • Love Connect +` : "Admin • Love Connect +";
    const e = await fetch(`${API}/admin/ai-engagement/stats`, { headers: authHeaders() });
    if (e.ok) setEngagement(await e.json());
  };
  const runBackfill = async () => {
    const r = await fetch(`${API}/admin/ai-engagement/backfill`, { method: "POST", headers: authHeaders() });
    if (r.ok) toast.success(`Matchs/messages programmés pour ${(await r.json()).scheduled_users} inscrit(s)`);
  };
  useEffect(() => {
    loadCount(); loadStats();
    const iv = setInterval(() => { loadStats(); loadCount(); }, 10000);
    return () => clearInterval(iv);
  }, []);
  const loadGallery = async () => {
    const r = await fetch(`${API}/admin/ai-profiles?limit=60`, { headers: authHeaders() });
    if (r.ok) setGallery(await r.json());
  };
  const toggleGallery = () => { const n = !showGallery; setShowGallery(n); if (n && gallery.length === 0) loadGallery(); };

  const startGeneration = async () => {
    const r = await fetch(`${API}/admin/generate-ai-profiles`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ count: Number(count), mode: genMode, base_url: process.env.REACT_APP_BACKEND_URL || window.location.origin }),
    });
    if (r.ok) {
      const j = await r.json(); setJob(j);
      if (j.message) toast.info(j.message);
      if (j.status === "running") pollJob(j.job_id);
    }
  };
  const pollJob = (jobId) => {
    const iv = setInterval(async () => {
      const r = await fetch(`${API}/admin/generation-status/${jobId}`, { headers: authHeaders() });
      if (r.ok) {
        const j = await r.json(); setJob(j);
        if (j.status !== "running") { clearInterval(iv); loadCount(); }
      }
    }, 4000);
  };

  if (denied) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4 p-6" data-testid="admin-denied">
      <p className="text-lg">Accès réservé à l'administrateur.</p>
      <button onClick={() => navigate("/login")} className="px-5 py-2 rounded-full bg-pink-600" data-testid="admin-login-redirect">Se connecter</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-panel">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-zinc-950/90 backdrop-blur border-b border-zinc-800 px-4 py-3 flex items-center justify-between" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/discover")} data-testid="admin-back"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="font-bold text-lg">Administration</h1>
        </div>
        <div className="flex items-center gap-2 text-sm text-zinc-400"><Users className="w-4 h-4" /> {aiCount} profils IA</div>
      </div>

      {/* Dashboard stats temps réel */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 p-3 border-b border-zinc-800 bg-zinc-950" data-testid="admin-stats">
          {[
            ["Inscrits réels", stats.real_users, "text-blue-400", () => goMembers("all")],
            ["Profils IA", stats.ai_profiles, "text-pink-400", () => { setTab("overview"); if (!showGallery) toggleGallery(); }],
            ["Anciens profils", stats.old_seed_profiles, "text-zinc-300", () => setTab("overview")],
            ["Total profils", stats.total_profiles, "text-emerald-400", () => goMembers("all")],
            ["Inscrits aujourd'hui", stats.signups_today, "text-yellow-400", () => goMembers("today")],
            ["Non lus (admin)", stats.admin_unread, "text-red-400", () => goInbox("unread")],
            ["Messages total", stats.messages_total, "text-purple-400", () => goInbox("all")],
          ].map(([label, val, cls, onClick], i) => (
            <button key={i} onClick={onClick} className="text-left bg-zinc-900 hover:bg-zinc-800 hover:ring-1 hover:ring-pink-600/50 transition-colors rounded-lg px-3 py-2" data-testid={`stat-${i}`}>
              <p className={`text-lg font-bold ${cls}`}>{(val ?? 0).toLocaleString("fr-FR")}</p>
              <p className="text-[10px] text-zinc-500 leading-tight">{label} ›</p>
            </button>
          ))}
          {stats.generation && stats.generation.status !== "completed" && (
            <div className="col-span-2 sm:col-span-4 lg:col-span-7 text-xs text-pink-400">
              <Loader2 className="inline w-3 h-3 animate-spin mr-1" />Génération en cours : {stats.generation.done}/{stats.generation.total}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-1 px-3 pt-3 border-b border-zinc-800 bg-zinc-950 overflow-x-auto" data-testid="admin-tabs">
        {[["overview", "Vue d'ensemble"], ["conversations", "Conversations"], ["members", "Membres"], ["purchases", "Achats"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`px-4 py-2 text-sm rounded-t-lg whitespace-nowrap ${tab === k ? "bg-black text-white border-x border-t border-zinc-800 font-semibold" : "text-zinc-400 hover:text-white"}`}>
            {l}{k === "conversations" && stats?.admin_unread ? <span className="ml-1.5 px-1.5 rounded-full bg-pink-600 text-[10px]">{stats.admin_unread}</span> : null}
          </button>
        ))}
      </div>

      {tab === "overview" && <>
      {engagement && (
        <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-950 flex items-center gap-3 flex-wrap text-xs text-zinc-400" data-testid="admin-engagement">
          <HeartHandshake className="w-4 h-4 text-emerald-400" />
          <span>Profils auto-match : <b className="text-emerald-400">{engagement.auto_match_profiles}</b></span>
          <span>Profils messages floutés : <b className="text-pink-400">{engagement.opener_profiles}</b></span>
          <span>Matchs auto créés : <b className="text-white">{engagement.auto_matches}</b></span>
          <span>Tâches en attente : <b className="text-yellow-400">{engagement.pending_jobs}</b></span>
          <button onClick={runBackfill} className="ml-auto px-3 py-1.5 rounded-full bg-emerald-600 text-white font-medium" data-testid="admin-backfill-btn">
            Appliquer aux inscrits existants
          </button>
        </div>
      )}

      {/* Generation panel */}
      <div className="p-4 border-b border-zinc-800 bg-zinc-950">
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkles className="w-4 h-4 text-pink-500" />
          <span className="text-sm text-zinc-300">Profils féminins IA :</span>
          <select value={genMode} onChange={(e) => setGenMode(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm" data-testid="gen-mode-select">
            <option value="total">Compléter jusqu'à un TOTAL de</option>
            <option value="add">Ajouter de NOUVEAUX profils</option>
          </select>
          <input type="number" min="1" max="3000" value={count} onChange={(e) => setCount(e.target.value)}
            className="w-24 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm" data-testid="gen-count-input" />
          <button onClick={startGeneration} className="px-4 py-1.5 rounded-full bg-pink-600 hover:bg-pink-500 text-sm font-semibold" data-testid="gen-start-btn">Générer</button>
          {job && (
            <span className="text-xs text-zinc-400" data-testid="gen-status">
              {job.status === "running"
                ? <><Loader2 className="inline w-3 h-3 animate-spin mr-1" />{job.done}/{job.total} créés{job.failed ? ` · ${job.failed} échec(s)` : ""} → objectif {job.target_total}</>
                : job.message || `✅ Terminé : ${job.done} créés (${job.failed} échecs)`}
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-500 mt-1">Aucun doublon : les profils déjà créés sont conservés. Une génération interrompue reprend automatiquement ; relancer ne recrée jamais un profil existant.</p>
        <button onClick={toggleGallery} className="mt-2 text-xs text-pink-400 underline" data-testid="toggle-gallery">
          {showGallery ? "Masquer la galerie des profils" : "Voir la galerie des profils IA"}
        </button>
        {showGallery && (
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2" data-testid="profiles-gallery">
            {gallery.map((p, i) => (
              <div key={i} className="bg-zinc-900 rounded-lg overflow-hidden cursor-pointer hover:ring-1 hover:ring-pink-600" onClick={() => setOpenMember(p.user_id)} data-testid={`gallery-item-${i}`}>
                <img src={p.main_photo} alt={p.name} className="w-full h-28 object-cover" />
                <div className="p-1.5 text-[10px] leading-tight">
                  <p className="font-semibold truncate">{p.name}, {p.age}</p>
                  <p className="text-zinc-400 truncate">{p.city}</p>
                  <p className="text-zinc-500">Ch: {p.hair} · Yx: {p.eyes}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      </>}

      {tab === "conversations" && <AdminInbox key={inboxFilter} initialFilter={inboxFilter} onDenied={() => setDenied(true)} onOpenMember={setOpenMember} />}
      {tab === "members" && <MembersTab initialFilter={memberFilter} onOpenMember={setOpenMember} />}
      {tab === "purchases" && <PurchasesTab onOpenMember={setOpenMember} />}
      {openMember && <MemberDrawer memberId={openMember} onClose={() => setOpenMember(null)} />}
    </div>
  );
}
